<template>
  <div class="flex-1 flex flex-col min-w-0 overflow-hidden">
    <div v-if="!store.currentProject.value" class="flex-1 flex items-center justify-center">
      <div class="w-full max-w-md px-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-1">Create a new project</h2>
        <p class="text-sm text-gray-500 mb-4">Or select an existing one from the top bar.</p>
        <form @submit.prevent="handleCreate" class="flex gap-2">
          <input
            v-model="newProjectName"
            type="text"
            placeholder="Project name"
            class="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            :disabled="creating"
          />
          <button
            type="submit"
            :disabled="!newProjectName.trim() || creating"
            class="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          >{{ creating ? 'Creating...' : 'Create' }}</button>
        </form>
        <p v-if="createError" class="mt-2 text-sm text-red-600">{{ createError }}</p>
      </div>
    </div>

    <!-- Plan tab: editor + generator side-by-side -->
    <div v-else-if="store.activeTab.value === 'plan'" class="flex-1 flex min-h-0 overflow-hidden">
      <!-- Left: PlanEditor (primary surface) -->
      <div class="flex-1 min-w-0 overflow-hidden flex flex-col">
        <!-- Mobile: button to open generator drawer -->
        <div class="md:hidden shrink-0 flex items-center justify-end px-4 py-2 border-b border-gray-200 bg-white">
          <button
            @click="planDrawerOpen = true"
            class="text-sm text-teal-700 font-medium hover:text-teal-900 transition-colors"
          >Idea Generator</button>
        </div>
        <PlanEditor />
      </div>

      <!-- Right: PlanGenerator — static on desktop, drawer on mobile -->
      <div
        class="fixed inset-y-0 right-0 z-40 w-[85vw] max-w-[400px] bg-white shadow-xl transform transition-transform duration-300
               md:static md:z-auto md:w-[400px] md:max-w-none md:transform-none md:shadow-none
               border-l border-gray-200 overflow-hidden flex flex-col flex-shrink-0"
        :class="planDrawerOpen ? 'translate-x-0' : 'translate-x-full md:translate-x-0'"
      >
        <!-- Mobile close button -->
        <div class="md:hidden flex items-center justify-between px-4 py-2 border-b border-gray-200">
          <span class="text-sm font-medium text-gray-700">Idea Generator</span>
          <button @click="planDrawerOpen = false" class="text-gray-500 hover:text-gray-700">
            <X :size="20" />
          </button>
        </div>
        <PlanGenerator />
      </div>
    </div>

    <!-- Ideas tab: full Idea Bank in main area, no right panel -->
    <div v-else-if="store.activeTab.value === 'ideas'" class="flex-1 min-h-0 overflow-y-auto">
      <div class="max-w-2xl mx-auto px-4 py-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Idea Bank</h2>

        <IdeaCaptureInput
          :saving="ideaSaving"
          @save="handleIdeaSave"
          @split-save="handleIdeaSplitSave"
        />

        <IdeaFilterBar
          v-model:status="ideaStore.statusFilter.value"
          v-model:search="ideaStore.searchQuery.value"
          :fresh-count="ideaStore.freshCount.value"
        />

        <div v-if="ideaStore.loading.value" class="text-sm text-gray-500 italic py-8 text-center">
          Loading ideas...
        </div>

        <IdeaList
          v-else
          :ideas="ideaStore.filteredIdeas.value"
          @update="handleIdeaUpdate"
          @delete="handleIdeaDelete"
          @archive="handleIdeaArchive"
        />
      </div>
    </div>

    <!-- Content generator view (default) -->
    <div v-else class="flex-1 flex min-h-0 overflow-hidden">
      <!-- Left/Center: Output (primary editing surface) -->
      <div class="flex-1 min-w-0 overflow-y-auto">
        <OutputColumn />
      </div>

      <!-- Right: Content inputs — static on desktop, drawer on mobile -->
      <div
        class="fixed inset-y-0 right-0 z-40 w-[85vw] max-w-[400px] bg-white shadow-xl transform transition-transform duration-300
               md:static md:z-auto md:w-[480px] md:max-w-none md:transform-none md:shadow-none
               border-l border-gray-200 overflow-y-auto flex-shrink-0"
        :class="store.rightDrawerOpen.value ? 'translate-x-0' : 'translate-x-full md:translate-x-0'"
      >
        <!-- Mobile close button -->
        <div class="md:hidden flex items-center justify-between px-4 py-2 border-b border-gray-200">
          <span class="text-sm font-medium text-gray-700">Brief & Settings</span>
          <button @click="store.rightDrawerOpen.value = false" class="text-gray-500 hover:text-gray-700">
            <X :size="20" />
          </button>
        </div>
        <div class="p-4">
          <InputColumn />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, watch } from 'vue'
import { X } from 'lucide-vue-next'
import InputColumn from './InputColumn.vue'
import OutputColumn from './OutputColumn.vue'
import PlanEditor from './PlanEditor.vue'
import PlanGenerator from './PlanGenerator.vue'
import IdeaCaptureInput from '../ideas/IdeaCaptureInput.vue'
import IdeaFilterBar from '../ideas/IdeaFilterBar.vue'
import IdeaList from '../ideas/IdeaList.vue'
import { useIdeaStore } from '../../composables/useIdeaStore'

const store = inject('workbench')
const planDrawerOpen = ref(false)
const newProjectName = ref('')
const creating = ref(false)
const createError = ref(null)

// Idea Bank state
const ideaStore = useIdeaStore()
const ideaSaving = ref(false)
let ideasLoaded = false

// Lazy-load ideas when tab first becomes active
watch(() => store.activeTab.value, (tab) => {
  if (tab === 'ideas' && !ideasLoaded) {
    ideasLoaded = true
    ideaStore.loadIdeas()
  }
})

async function handleIdeaSave(body) {
  ideaSaving.value = true
  try { await ideaStore.createIdea(body) } finally { ideaSaving.value = false }
}

async function handleIdeaSplitSave(bodies) {
  ideaSaving.value = true
  try { await ideaStore.batchCreate(bodies) } finally { ideaSaving.value = false }
}

async function handleIdeaUpdate(id, params) { await ideaStore.updateIdea(id, params) }
async function handleIdeaDelete(id) { await ideaStore.deleteIdea(id) }
async function handleIdeaArchive(id) { await ideaStore.archiveIdea(id) }

async function handleCreate() {
  if (!newProjectName.value.trim()) return
  creating.value = true
  createError.value = null
  try {
    await store.createProject(newProjectName.value.trim())
    newProjectName.value = ''
  } catch (e) {
    createError.value = e.message || 'Failed to create project'
    console.error('Create project error:', e)
  } finally {
    creating.value = false
  }
}
</script>
